<template>
  <div class="uploader-wrap" :class="{ disabled }">
    <img
      v-if="src"
      v-show="!editMode"
      :src="src"
      class="existing-image"
      @click="$emit('image-click')"
    />

    <vue-dropzone
      v-if="editMode || !src"
      :id="id"
      ref="vueDropzone"
      :class="{ 'uploader-inline': inline, 'bg-white': bgWhite }"
      :options="dropzoneOptions"
      :use-custom-slot="true"
      :awss3="awss3"
      :disabled="disabled"
      @vdropzone-s3-upload-error="s3UploadError"
      @vdropzone-s3-upload-success="s3UploadSuccess"
      @vdropzone-file-added="onFileAdded"
      @vdropzone-removed-file="onFileRemoved"
      @vdropzone-error="$emit('uploading-failed')"
      @vdropzone-canceled="$emit('uploading-failed')"
    >
      <div class="content">
        <v-icon name="cloud-upload-alt" class="upload-icon" />
        <h3 class="title">
          Drag and drop to upload!
        </h3>
        <p class="subtitle">
          (or click to choose a file)
        </p>
        <p class="info">
          .jpg or.png files only. Max size: {{ maxFileSize }}mb
        </p>
      </div>
    </vue-dropzone>
  </div>
</template>

<script>
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import { getCognitoToken } from "@/lib/auth";

// TODO: This is an async action and should probably be called in the data prop or a computed prop instead of here 🤷‍♂️ Not sure how this is working!
const accessToken = getCognitoToken();

export default {
  name: "Uploader",
  components: {
    vueDropzone: vue2Dropzone,
  },
  props: {
    bgWhite: {
      default: false,
      type: Boolean,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    editMode: Boolean,
    src: {
      type: String,
      default: "",
    },
    id: {
      type: String,
      required: true,
    },
    inline: {
      default: false,
      type: Boolean,
    },
    maxFileSize: {
      default: 2.5,
      type: Number,
    },
    onImageClick: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      awss3: {
        signingURL: (f) =>
          `${process.env.VUE_APP_SERVICES_URL}/s3/signUrl?fname=${f.name}`,
        headers: {
          authorization: accessToken ? `Bearer ${accessToken}` : "",
        },
        sendFileToServer: false,
        withCredentials: false,
      },
      dropzoneOptions: {
        autoProcessQueue: false, // disabled, see onFileAdded below
        maxFilesize: this.maxFileSize,
        maxFiles: 1,
        addRemoveLinks: true,
        acceptedFiles: "image/jpeg, image/png, image/gif",
        uploadMultiple: false,
      },
    };
  },
  methods: {
    onFileAdded() {
      this.$emit("uploading-file");

      // We need a setTimeout so that dropzone can run it's file validators
      setTimeout(() => {
        // Process the queue after the timeout...
        this.$refs.vueDropzone.processQueue();
      }, 500);
    },
    onFileRemoved() {
      this.$emit("file-removed");
    },
    s3UploadError(error) {
      this.$emit("uploading-failed", error);
    },
    s3UploadSuccess(s3ObjectLocation) {
      this.$emit("uploading-complete", s3ObjectLocation);
    },
  },
};
</script>

<style scoped>
.uploader-wrap {
  &.disabled {
    @apply pointer-events-none;
    opacity: 0.6;
  }
}

.vue-dropzone {
  @apply rounded-lg p-2 border border-grey-light bg-brand-light shadow;

  &.dz-started {
    @apply rounded-none p-0 border-none bg-transparent shadow-none;
  }

  &:hover {
    @apply bg-brand-light;
  }

  >>> .dz-preview .dz-image img {
    @apply m-0 rounded;
  }

  &.bg-white {
    @apply bg-white;
  }

  @screen md {
    @apply p-3;
  }
}

>>> .dropzone .dz-preview.dz-image-preview {
  @apply rounded-lg shadow border border-grey-light m-0;
  padding: 5px;
}

/* >>> .dropzone .dz-preview {
  min-width: 60%;
} */

>>> .dropzone .dz-preview.dz-error .dz-image {
  @apply opacity-50;
}

>>> .dropzone .dz-preview .dz-details {
  @apply rounded;
  background: color-mod(#6574cd alpha(80%));
}

>>> .vue-dropzone .dz-preview .dz-remove {
  @apply rounded bg-danger border-none px-3 py-2 font-lato;
  font-size: 12px;

  &:hover {
    @apply no-underline bg-danger-dark;
  }
}

>>> .dropzone .dz-message {
  @apply m-0;
}

>>> .dropzone .dz-preview.dz-error {
  @apply bg-danger;

  .dz-error-message {
    @apply text-xs leading-normal;
    background: #eb485f;
    left: 12px;
    top: 5px;
    width: 89%;

    &:after {
      border-top-color: #eb485f;
      bottom: -5px;
    }
  }
}

>>> .dropzone .dz-preview .dz-progress {
  @apply shadow border border-2 border-brand;
  height: 21px;
  width: 89px;
}

>>> .vue-dropzone .dz-preview .dz-progress .dz-upload {
  @apply bg-success opacity-75;
}

.upload-icon {
  @apply mb-0;
  color: #d7dcf7;
  font-size: 3em;
}

.content {
  @apply cursor-pointer select-none text-center;
}

.title {
  @apply mt-0 mb-1 text-brand leading-normal text-base;
}

.subtitle {
  @apply text-grey-dark mx-auto mb-2 text-xs;
}

.info {
  @apply text-grey mx-auto text-xs mb-0;
  max-width: 70%;
}

.uploader-inline {
  min-height: 0;

  >>> .dz-message {
    @apply m-0;
  }

  >>> .content {
    @apply text-left relative;
  }

  >>> .upload-icon {
    @apply hidden;
  }

  >>> .title {
    @apply m-0;
    font-size: 14px;
  }

  >>> .subtitle,
  >>> .info {
    @apply m-0 max-w-full;
    font-size: 12px;
  }

  >>> .info {
    @apply text-grey;
    font-size: 11px;
  }
}

.existing-image {
  @apply rounded-lg border border-grey-light border-solid bg-white shadow mb-0 w-full cursor-pointer;
  padding: 5px;
}
</style>
